import React, { useState, useEffect } from "react";
import {
  Modal,
  TextField,
  Autocomplete,
  Grid,
  Button,
  Typography,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { BASE_URL } from "./../../global";
import { toast } from "react-toastify";
import axiosWithToken from "./axiosTokenConfig";

const MultiValueInputModal = ({
  buttonLabel,
  stateKey,
  inputStates,
  updateInputObject,
  groupId,
}) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([
    { min: "", max: "", minUnit: "", maxUnit: "" },
  ]);
  const [unitArray, setUnitArray] = useState([]);
  const [unitFilter, setUnitFilter] = useState([]);

  // ---------------- Modal Handlers ----------------
  const handleOpen = () => {
    setOpen(true);

    const groups = inputStates?.[stateKey]?.split("||");
    const result = groups?.map((group) => {
      const parts = group?.split("|");
      let min, max, minUnit, maxUnit;

      if (parts.length === 1) {
        min = parts[0]?.split("#")[0];
        max = "";
        minUnit = parts[0]?.split("#")[1];
        maxUnit = "";
      } else if (parts.length === 2) {
        min = parts[0]?.split("#")[0];
        max = parts[1]?.split("#")[0];
        minUnit = parts[0]?.split("#")[1];
        maxUnit = parts[1]?.split("#")[1];
      }

      return { min, max, minUnit, maxUnit };
    });

    inputStates?.[stateKey] && setRows(result);
  };

  const handleClose = () => {
    updateInputObject(
      stateKey,
      rows
        .map((row) => {
          const minValue = `${row.min}#${row.minUnit}`;
          const hasMaxValue = row.max && row.max !== "";
          const maxValue = hasMaxValue ? `|${row.max}#${row.maxUnit}` : "";
          return `${minValue}${maxValue}`;
        })
        .join("||")
    );
    setOpen(false);
  };

  // ---------------- Row Handlers ----------------
  const handleAddRow = () => {
    setRows([...rows, { min: "", max: "", minUnit: "", maxUnit: "" }]);
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const handleDeleteRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  // ---------------- API Call ----------------
  const getUnitMasterArray = () => {
    axiosWithToken
      .get(`${BASE_URL}unit`)
      .then((res) => {
        setUnitArray(res.data || []);
      })
      .catch(() => {
        toast.error("Something went wrong while fetching units!");
      });
  };

  useEffect(() => {
    if (open) {
      getUnitMasterArray();
    }
  }, [open]);

  useEffect(() => {
    if (unitArray.length) {
      setUnitFilter([
        "",
        ...unitArray
          .filter((u) => !groupId || u.groupId == groupId)
          .map((u) => ({ label: u.symbol })),
      ]);
    }
  }, [unitArray, groupId]);

  // ---------------- UI ----------------
  return (
    <div>
      <Button
        variant="outlined"
        size="small"
        onClick={handleOpen}
        sx={{
          backgroundColor: inputStates?.[stateKey] ? "#a5d6a7" : "transparent", // light green if value present
          "&:hover": {
            backgroundColor: inputStates?.[stateKey]
              ? "#a5d6a7"
              : "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        {buttonLabel}
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            padding: "20px",
            width: "56%",
          }}
        >
          {/* Header */}
          <div
            style={{
              justifyContent: "space-between",
              padding: "25px",
              display: "flex",
              width: "100%",
            }}
          >
            <Typography variant="body1" color="initial"></Typography>
            <Button variant="contained" onClick={handleClose}>
              X
            </Button>
          </div>

          {/* Rows */}
          <div
            style={{
              border: "1px solid black",
              width: "95%",
              margin: "auto",
              padding: "30px",
              marginBottom: "15px",
            }}
          >
            {rows?.map((row, index) => (
              <Grid
                key={index}
                container
                alignItems="center"
                spacing={2}
                style={{ marginBottom: "10px" }}
              >
                {/* Min Value */}
                <Grid item>
                  <TextField
                    type="text"
                    label={stateKey !== "masterleastcount" ? `Lower ${buttonLabel}` : `${buttonLabel}`}
                    value={row.min}
                    size="small"
                    inputProps={{ autoComplete: "off" }}
                    onChange={(e) =>
                      handleInputChange(index, "min", e.target.value)
                    }
                    sx={{ width: "120px" }}
                  />
                </Grid>

                {/* Min Unit */}
                <Grid item>
                  <Autocomplete
                    size="small"
                    options={unitFilter}
                    getOptionLabel={(option) => option.label || ""}
                    value={
                      unitFilter.find((lab) => lab.label === row.minUnit) ||
                      null
                    }
                    sx={{ minWidth: "120px" }}
                    renderInput={(params) => (
                      <TextField {...params} label="Unit*" />
                    )}
                    onChange={(event, value) => {
                      if (value)
                        handleInputChange(index, "minUnit", value.label);
                    }}
                  />
                </Grid>

                {/* Max Value + Unit (optional) */}
                {stateKey !== "masterleastcount" &&
                  stateKey !== "expandedUncertainty" &&
                  stateKey !== "accuracy" && (
                    <>
                      <Grid item>
                        <span style={{ margin: "5px", fontWeight: "bold" }}>
                          TO
                        </span>
                      </Grid>
                      <Grid item>
                        <TextField
                          type="text"
                          label={`Higher ${buttonLabel}`}
                          value={row.max}
                          inputProps={{ autoComplete: "off" }}
                          size="small"
                          onChange={(e) =>
                            handleInputChange(index, "max", e.target.value)
                          }
                          sx={{ width: "120px" }}
                        />
                      </Grid>
                      <Grid item>
                        <Autocomplete
                          size="small"
                          options={unitFilter}
                          getOptionLabel={(option) => option.label || ""}
                          value={
                            unitFilter.find(
                              (lab) => lab.label === row.maxUnit
                            ) || null
                          }
                          sx={{ minWidth: "120px" }}
                          renderInput={(params) => (
                            <TextField {...params} label="Unit*" />
                          )}
                          onChange={(event, value) => {
                            if (value)
                              handleInputChange(index, "maxUnit", value.label);
                          }}
                        />
                      </Grid>
                    </>
                  )}

                {/* Delete Icon */}
                <Grid item>
                  <DeleteIcon
                    onClick={() => handleDeleteRow(index)}
                    style={{
                      cursor: "pointer",
                      color: "red",
                      fontSize: "28px",
                      marginTop: "4px",
                    }}
                  />
                </Grid>
              </Grid>
            ))}
          </div>

          {/* Add Row Button */}
          <Button variant="outlined" size="small" onClick={handleAddRow}>
            Add Row
          </Button>
        </Box>
      </Modal>
    </div>
  );
};

export default MultiValueInputModal;
